import React, { useState } from 'react';
import {
  Box, Grid, Card, CardContent, Typography, Chip, Stack, Avatar,
  LinearProgress, Divider, IconButton, Tooltip, alpha,
} from '@mui/material';
import TableChartIcon      from '@mui/icons-material/TableChart';
import PeopleIcon          from '@mui/icons-material/People';
import AccountBalanceIcon  from '@mui/icons-material/AccountBalance';
import AssessmentIcon      from '@mui/icons-material/Assessment';
import TrendingUpIcon      from '@mui/icons-material/TrendingUp';
import StorageIcon         from '@mui/icons-material/Storage';
import BusinessIcon        from '@mui/icons-material/Business';
import DateRangeIcon       from '@mui/icons-material/DateRange';
import LayersIcon          from '@mui/icons-material/Layers';
import AttachMoneyIcon     from '@mui/icons-material/AttachMoney';
import GavelIcon           from '@mui/icons-material/Gavel';
import CreditCardIcon      from '@mui/icons-material/CreditCard';
import ArrowForwardIcon    from '@mui/icons-material/ArrowForward';
import ArrowUpwardIcon     from '@mui/icons-material/ArrowUpward';
import CheckCircleIcon     from '@mui/icons-material/CheckCircle';
import ScheduleIcon        from '@mui/icons-material/Schedule';
import AccountTreeIcon     from '@mui/icons-material/AccountTree';
import OpenInNewIcon       from '@mui/icons-material/OpenInNew';
import { tableLabel }      from '../config/tableLabels';

const CATEGORY_CONFIG = {
  'Org Structure':    { color: '#16a34a', bg: 'linear-gradient(135deg,#16a34a,#22c55e)', icon: <BusinessIcon />,      tables: ['LEGISLATION_DATA_GROUP','DEPARTMENT','ROLES','DESIGNATION','BUSINESS_TYPE','COMPANY','LOCATION','BUSINESS_GROUP','MY_MODULES'] },
  'Pay Foundation':   { color: '#059669', bg: 'linear-gradient(135deg,#059669,#22c55e)', icon: <DateRangeIcon />,     tables: ['PERIOD_TYPE','PAY_PERIOD_STATUS','CONSOLIDATION_GROUP','PAYROLL','PAY_PERIOD'] },
  'Element Setup':    { color: '#16a34a', bg: 'linear-gradient(135deg,#16a34a,#4ade80)', icon: <LayersIcon />,        tables: ['ELEMENT_TYPE','ELEMENT_INPUT_VALUE','ELEMENT_ELIGIBILITY','PAYROLL_FORMULA'] },
  'Salary & Entries': { color: '#DB2777', bg: 'linear-gradient(135deg,#DB2777,#f472b6)', icon: <AttachMoneyIcon />,   tables: ['SALARY','SALARY_REVISION_HISTORY','ELEMENT_ENTRY','ELEMENT_ENTRY_VALUE'] },
  'Workforce':        { color: '#D97706', bg: 'linear-gradient(135deg,#D97706,#f59e0b)', icon: <PeopleIcon />,        tables: ['PAYROLL_RELATIONSHIP','ATTENDANCE','LEAVE_BALANCE'] },
  'Tax & Statutory':  { color: '#DC2626', bg: 'linear-gradient(135deg,#DC2626,#f87171)', icon: <GavelIcon />,         tables: ['TAX_DECLARATION','TAX_REGIME_CONFIG','FORM16_SUMMARY','PF_ACCOUNT','ESI_ACCOUNT'] },
  'Advance Salary Structure': { color: '#F59E0B', bg: 'linear-gradient(135deg,#F59E0B,#fbbf24)', icon: <AccountBalanceIcon />,tables: ['ADVANCE_SALARY','ADVANCE_SALARY_REPAYMENT'] },
  'Payroll Run':      { color: '#047857', bg: 'linear-gradient(135deg,#047857,#059669)', icon: <TrendingUpIcon />,    tables: ['PAYROLL_RUN','RUN_RESULT','RUN_RESULT_VALUE'] },
  'Payments':         { color: '#DB2777', bg: 'linear-gradient(135deg,#DB2777,#e11d48)', icon: <CreditCardIcon />,    tables: ['PERSONAL_PAYMENT_METHOD','PAYMENT','PAYSLIP','BANK_PAYMENT_FILE'] },
  'Balances & GL':    { color: '#047857', bg: 'linear-gradient(135deg,#047857,#059669)', icon: <AssessmentIcon />,   tables: ['BALANCE_DEFINITION','DEFINED_BALANCE','BALANCE_VALUE','PAYROLL_COSTING','GL_ACCOUNT_MASTER'] },
};

const QUICK_ACCESS = [
  { name: 'COMPANY',              label: 'Company',           icon: <BusinessIcon sx={{ fontSize: 16 }} />,       color: '#22c55e' },
  { name: 'PAYROLL',              label: 'Payroll',           icon: <AttachMoneyIcon sx={{ fontSize: 16 }} />,    color: '#059669' },
  { name: 'PAY_PERIOD',           label: 'Pay Period',        icon: <DateRangeIcon sx={{ fontSize: 16 }} />,      color: '#16a34a' },
  { name: 'EMPLOYEE',             label: 'Employees',         icon: <PeopleIcon sx={{ fontSize: 16 }} />,         color: '#D97706' },
  { name: 'PAYROLL_RUN',          label: 'Payroll Run',       icon: <TrendingUpIcon sx={{ fontSize: 16 }} />,     color: '#047857' },
  { name: 'ELEMENT_TYPE',         label: 'Elements',          icon: <LayersIcon sx={{ fontSize: 16 }} />,         color: '#DB2777' },
  { name: 'TAX_DECLARATION',      label: 'Tax',               icon: <GavelIcon sx={{ fontSize: 16 }} />,          color: '#DC2626' },
  { name: 'BUSINESS_GROUP',       label: 'Business Group',    icon: <AccountTreeIcon sx={{ fontSize: 16 }} />,    color: '#F59E0B' },
];

const STATUS_ITEMS = [
  { label: 'System Status',  value: 'Operational',  icon: <CheckCircleIcon sx={{ fontSize: 14 }} />, color: '#059669', bg: '#DCFCE7' },
  { label: 'Data Mode',      value: 'PostgreSQL',   icon: <StorageIcon sx={{ fontSize: 14 }} />,      color: '#22c55e', bg: '#dcfce7' },
  { label: 'Build',          value: 'Development',  icon: <ScheduleIcon sx={{ fontSize: 14 }} />,     color: '#D97706', bg: '#FEF3C7' },
  { label: 'Schema',         value: 'Oracle-Aligned',icon: <AssessmentIcon sx={{ fontSize: 14 }} />,  color: '#059669', bg: '#ecfdf5' },
];

export default function Dashboard({ tables, onSelectTable }) {
  const [hoveredCategory, setHoveredCategory] = useState(null);

  const orgTables = ['LEGISLATION_DATA_GROUP','COMPANY','LOCATION','BUSINESS_GROUP','DEPARTMENT','ROLES','DESIGNATION','MY_MODULES'];
  const payTables = ['PAYROLL','PAY_PERIOD','PAYROLL_RUN','SALARY'];

  const totalRecords = tables.reduce((s, t) => s + t.count, 0);
  const activeTables = tables.filter(t => t.count > 0).length;

  const stats = [
    {
      label: 'Total Tables', value: tables.length,
      sub: `${activeTables} with data`,
      icon: <StorageIcon />, gradient: 'linear-gradient(135deg,#16a34a,#22c55e)',
      progress: (activeTables / tables.length) * 100,
    },
    {
      label: 'Total Records', value: totalRecords,
      sub: 'across all tables',
      icon: <AssessmentIcon />, gradient: 'linear-gradient(135deg,#059669,#22c55e)',
      progress: Math.min((totalRecords / 500) * 100, 100),
    },
    {
      label: 'Org Tables', value: orgTables.filter(n => tables.find(t => t.name === n)).length,
      sub: `of ${orgTables.length} configured`,
      icon: <BusinessIcon />, gradient: 'linear-gradient(135deg,#16a34a,#4ade80)',
      progress: (orgTables.filter(n => tables.find(t => t.name === n)).length / orgTables.length) * 100,
    },
    {
      label: 'Pay Tables', value: payTables.filter(n => tables.find(t => t.name === n)).length,
      sub: `of ${payTables.length} configured`,
      icon: <AttachMoneyIcon />, gradient: 'linear-gradient(135deg,#DB2777,#f472b6)',
      progress: (payTables.filter(n => tables.find(t => t.name === n)).length / payTables.length) * 100,
    },
  ];

  const getTable = (name) => tables.find(t => t.name === name);

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: 1400, mx: 'auto' }}>

      {/* ── Hero Banner ────────────────────────────────────────────── */}
      <Box sx={{
        borderRadius: '20px', mb: 3, p: { xs: 2.5, sm: 3.5 },
        overflow: 'hidden', position: 'relative',
        background: 'linear-gradient(135deg, #052e16 0%, #14532d 45%, #0f4d28 100%)',
        boxShadow: '0 24px 48px rgba(34,197,94,0.18), 0 8px 16px rgba(0,0,0,0.2)',
      }}>
        {/* Dot grid overlay */}
        <Box sx={{
          position: 'absolute', inset: 0, opacity: 0.15,
          backgroundImage: 'radial-gradient(rgba(134,239,172,0.6) 1px, transparent 1px)',
          backgroundSize: '24px 24px',
        }} />
        {/* Glow orbs */}
        <Box sx={{ position:'absolute', right: -60, top: -60, width: 260, height: 260,
          borderRadius:'50%', background: 'radial-gradient(circle, rgba(34,197,94,0.2) 0%, transparent 70%)' }} />
        <Box sx={{ position:'absolute', left: '40%', bottom: -80, width: 200, height: 200,
          borderRadius:'50%', background: 'radial-gradient(circle, rgba(22,163,74,0.12) 0%, transparent 70%)' }} />

        <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ sm: 'center' }}
          justifyContent="space-between" gap={2} sx={{ position: 'relative' }}>
          <Box>
            <Stack direction="row" alignItems="center" gap={1} mb={1}>
              <Chip label="DEVELOPMENT MODE" size="small" sx={{
                bgcolor: 'rgba(34,197,94,0.25)', color: '#86efac',
                fontSize: 9, fontWeight: 800, letterSpacing: 1.2, height: 20, borderRadius: '6px',
              }} />
              <Chip label="v1.0.0" size="small" sx={{
                bgcolor: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.4)',
                fontSize: 9, fontWeight: 700, height: 20, borderRadius: '6px',
              }} />
            </Stack>
            <Typography variant="h4" color="white" fontWeight={900} lineHeight={1.1} mb={0.75}
              sx={{ fontSize: { xs: 22, sm: 28 } }}>
              VVSPL Payroll HRMS
            </Typography>
            <Typography sx={{ color: 'rgba(255,255,255,0.45)', fontSize: 13 }}>
              Oracle Fusion–aligned · {tables.length} tables · {totalRecords} records
            </Typography>
          </Box>

          {/* Status badges */}
          <Stack direction="row" gap={1} flexWrap="wrap">
            {STATUS_ITEMS.map(s => (
              <Box key={s.label} sx={{
                px: 1.5, py: 0.8, borderRadius: '12px',
                bgcolor: 'rgba(255,255,255,0.06)',
                border: '1px solid rgba(255,255,255,0.1)',
                backdropFilter: 'blur(8px)',
                display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 80,
              }}>
                <Box sx={{ color: s.bg.replace('FE', 'A0'), mb: 0.25, display: 'flex' }}>
                  {React.cloneElement(s.icon, { sx: { fontSize: 12, color: 'rgba(255,255,255,0.35)' } })}
                </Box>
                <Typography fontSize={10} fontWeight={700} color="rgba(255,255,255,0.85)" lineHeight={1.2}>
                  {s.value}
                </Typography>
                <Typography fontSize={8.5} color="rgba(255,255,255,0.35)" letterSpacing={0.3}>
                  {s.label}
                </Typography>
              </Box>
            ))}
          </Stack>
        </Stack>
      </Box>

      {/* ── KPI Stat Cards ─────────────────────────────────────────── */}
      <Grid container spacing={2} mb={3}>
        {stats.map(s => (
          <Grid item xs={6} lg={3} key={s.label}>
            <Card sx={{
              border: '1px solid #E8EDF5',
              boxShadow: '0 2px 8px rgba(0,0,0,0.05), 0 0 0 1px rgba(0,0,0,0.02)',
              borderRadius: '16px',
              transition: 'all 0.2s ease',
              '&:hover': {
                boxShadow: '0 8px 24px rgba(0,0,0,0.1)',
                transform: 'translateY(-2px)',
              },
            }}>
              <CardContent sx={{ p: 2.5, pb: '20px !important' }}>
                <Stack direction="row" alignItems="flex-start" justifyContent="space-between" mb={1.5}>
                  <Box>
                    <Typography variant="h3" fontWeight={900} lineHeight={1} mb={0.4}
                      sx={{
                        fontSize: { xs: 26, sm: 30 },
                        background: s.gradient,
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                      }}>
                      {s.value}
                    </Typography>
                    <Typography fontSize={12} fontWeight={700} color="text.primary" mb={0.2}>
                      {s.label}
                    </Typography>
                    <Typography fontSize={10.5} color="text.secondary">{s.sub}</Typography>
                  </Box>
                  <Avatar sx={{
                    width: 44, height: 44, background: s.gradient,
                    boxShadow: '0 6px 16px rgba(0,0,0,0.18)',
                    borderRadius: '12px',
                  }}>
                    {React.cloneElement(s.icon, { sx: { fontSize: 22 } })}
                  </Avatar>
                </Stack>
                <LinearProgress
                  variant="determinate" value={s.progress}
                  sx={{
                    height: 3, borderRadius: 4, bgcolor: '#F1F5F9',
                    '& .MuiLinearProgress-bar': {
                      background: s.gradient, borderRadius: 4,
                    },
                  }}
                />
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* ── Quick Access ───────────────────────────────────────────── */}
      <Box mb={3}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1.5}>
          <Typography fontSize={13} fontWeight={700} color="text.secondary" letterSpacing={0.5}
            textTransform="uppercase">
            Quick Access
          </Typography>
        </Stack>
        <Box sx={{
          display: 'flex', gap: 1.5, overflowX: 'auto', pb: 0.5,
          '&::-webkit-scrollbar': { height: 0 },
        }}>
          {QUICK_ACCESS.map(q => {
            const tbl = getTable(q.name);
            if (!tbl) return null;
            return (
              <Box
                key={q.name}
                onClick={() => onSelectTable(q.name)}
                sx={{
                  flexShrink: 0, px: 2, py: 1.25, borderRadius: '12px',
                  border: '1.5px solid #E8EDF5', bgcolor: 'white',
                  cursor: 'pointer', transition: 'all 0.18s ease',
                  display: 'flex', alignItems: 'center', gap: 1.2,
                  minWidth: 130,
                  '&:hover': {
                    borderColor: q.color,
                    bgcolor: alpha(q.color, 0.05),
                    boxShadow: `0 4px 14px ${alpha(q.color, 0.18)}`,
                    transform: 'translateY(-1px)',
                  },
                }}
              >
                <Box sx={{
                  width: 30, height: 30, borderRadius: '8px',
                  bgcolor: alpha(q.color, 0.1),
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: q.color, flexShrink: 0,
                }}>
                  {q.icon}
                </Box>
                <Box>
                  <Typography fontSize={12} fontWeight={700} color="text.primary" lineHeight={1.2} noWrap>
                    {q.label}
                  </Typography>
                  {tbl?.count > 0 && (
                    <Typography fontSize={10} color="text.secondary">{tbl.count} records</Typography>
                  )}
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>

      {/* ── Module Categories ──────────────────────────────────────── */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
        <Typography fontSize={13} fontWeight={700} color="text.secondary" letterSpacing={0.5}
          textTransform="uppercase">
          All Modules
        </Typography>
        <Typography fontSize={11} color="text.disabled">
          {Object.keys(CATEGORY_CONFIG).length} categories
        </Typography>
      </Stack>

      <Grid container spacing={2}>
        {Object.entries(CATEGORY_CONFIG).map(([category, cfg]) => {
          const available = cfg.tables.filter(n => getTable(n));
          if (!available.length) return null;
          const totalRec   = available.reduce((s, n) => s + (getTable(n)?.count || 0), 0);
          const isHovered  = hoveredCategory === category;

          return (
            <Grid item xs={12} sm={6} lg={4} key={category}>
              <Card
                onMouseEnter={() => setHoveredCategory(category)}
                onMouseLeave={() => setHoveredCategory(null)}
                sx={{
                  height: '100%',
                  border: '1.5px solid',
                  borderColor: isHovered ? alpha(cfg.color, 0.4) : '#E8EDF5',
                  borderRadius: '18px',
                  overflow: 'hidden',
                  transition: 'all 0.22s ease',
                  boxShadow: isHovered
                    ? `0 12px 32px ${alpha(cfg.color, 0.15)}, 0 2px 8px rgba(0,0,0,0.06)`
                    : '0 2px 8px rgba(0,0,0,0.04)',
                  transform: isHovered ? 'translateY(-3px)' : 'none',
                }}
              >
                {/* Colored accent stripe at top */}
                <Box sx={{ height: 4, background: cfg.bg }} />

                {/* Category header */}
                <Box sx={{ px: 2.5, pt: 2, pb: 1.5 }}>
                  <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Stack direction="row" alignItems="center" gap={1.5}>
                      <Box sx={{
                        width: 38, height: 38, borderRadius: '10px',
                        background: cfg.bg,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        boxShadow: `0 4px 12px ${alpha(cfg.color, 0.35)}`,
                      }}>
                        {React.cloneElement(cfg.icon, { sx: { fontSize: 18, color: 'white' } })}
                      </Box>
                      <Box>
                        <Typography fontWeight={800} color="text.primary" fontSize={14} lineHeight={1.2}>
                          {category}
                        </Typography>
                        <Stack direction="row" gap={0.75} mt={0.3}>
                          <Chip
                            label={`${available.length} tables`}
                            size="small"
                            sx={{
                              height: 17, fontSize: 9.5, fontWeight: 700,
                              bgcolor: alpha(cfg.color, 0.1), color: cfg.color,
                              borderRadius: '5px',
                            }}
                          />
                          {totalRec > 0 && (
                            <Chip
                              label={`${totalRec} records`}
                              size="small"
                              sx={{
                                height: 17, fontSize: 9.5, fontWeight: 700,
                                bgcolor: '#F1F5F9', color: '#64748B',
                                borderRadius: '5px',
                              }}
                            />
                          )}
                        </Stack>
                      </Box>
                    </Stack>
                  </Stack>
                </Box>

                <Divider sx={{ borderColor: '#F1F5F9' }} />

                {/* Table list */}
                <Box sx={{ px: 1.5, py: 1 }}>
                  {available.map((name, idx) => {
                    const tbl = getTable(name);
                    return (
                      <Box
                        key={name}
                        onClick={() => onSelectTable(name)}
                        sx={{
                          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                          px: 1.25, py: 0.85, borderRadius: '10px', cursor: 'pointer',
                          transition: 'all 0.15s ease',
                          mt: idx === 0 ? 0 : 0.25,
                          '&:hover': {
                            bgcolor: alpha(cfg.color, 0.07),
                            '& .row-arrow': { opacity: 1, transform: 'translateX(0)' },
                            '& .row-label': { color: cfg.color },
                          },
                        }}
                      >
                        <Stack direction="row" alignItems="center" gap={1.2}>
                          <Box sx={{
                            width: 24, height: 24, borderRadius: '6px',
                            bgcolor: alpha(cfg.color, 0.1),
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            flexShrink: 0,
                          }}>
                            <TableChartIcon sx={{ fontSize: 11, color: cfg.color }} />
                          </Box>
                          <Typography
                            className="row-label"
                            fontSize={12.5} fontWeight={500} color="text.primary"
                            sx={{ transition: 'color 0.15s' }}
                          >
                            {tableLabel(name)}
                          </Typography>
                        </Stack>

                        <Stack direction="row" alignItems="center" gap={0.75}>
                          {tbl?.count > 0 && (
                            <Box sx={{
                              px: 1, py: 0.15, borderRadius: '20px',
                              bgcolor: alpha(cfg.color, 0.1),
                              minWidth: 26, textAlign: 'center',
                            }}>
                              <Typography fontSize={10} fontWeight={700} sx={{ color: cfg.color }}>
                                {tbl.count}
                              </Typography>
                            </Box>
                          )}
                          <ArrowForwardIcon
                            className="row-arrow"
                            sx={{
                              fontSize: 13, color: cfg.color,
                              opacity: 0, transform: 'translateX(-6px)',
                              transition: 'all 0.15s ease',
                            }}
                          />
                        </Stack>
                      </Box>
                    );
                  })}
                </Box>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {/* ── Footer info ────────────────────────────────────────────── */}
      <Box sx={{ mt: 4, pt: 2.5, borderTop: '1px solid #E8EDF5' }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} alignItems="center"
          justifyContent="space-between" gap={1}>
          <Typography fontSize={11.5} color="text.disabled">
            VVSPL HRMS · Oracle Fusion Global Payroll Aligned · {tables.length} Tables · In-memory store
          </Typography>
          <Stack direction="row" gap={1}>
            {['Normalized', 'Date-Effective', 'RLS-Ready', 'Multi-Org'].map(tag => (
              <Chip key={tag} label={tag} size="small" sx={{
                height: 18, fontSize: 9.5, bgcolor: '#F1F5F9', color: '#94A3B8', fontWeight: 600,
              }} />
            ))}
          </Stack>
        </Stack>
      </Box>

    </Box>
  );
}
